'use strict';

import Swiper from 'swiper';
import { A11y, Keyboard, Navigation, Pagination, Thumbs } from 'swiper/modules';
import { decorateCarouselPagination, formatCarouselLabel, updateCarouselLiveRegion } from '../utils/lvzCarouselA11y';

let stickySidebar = require('../utils/customStickySidebar');

(function initializeImageCarousel() {
	let swatchGalleryUpdateForRequest = false;

	/**
	 * @param {string} value
	 * @returns {string}
	 */
	function escapeHtml(value) {
		return String(value == null ? '' : value)
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;');
	}

	/**
	 * @param {Element} gallery
	 * @returns {{ promo: string, newIn: string, bestSellers: string, onlineExclusive: string }}
	 */
	function getBadgeLabels(gallery) {
		return {
			promo: gallery.getAttribute('data-badge-promo') || 'PROMO',
			newIn: gallery.getAttribute('data-badge-new-in') || 'NEW IN',
			bestSellers: gallery.getAttribute('data-badge-best-sellers') || 'BEST SELLER',
			onlineExclusive: gallery.getAttribute('data-badge-online-exclusive') || 'Online exclusive',
		};
	}

	/**
	 * @param {Object} tags
	 * @param {{ promo: string, newIn: string, bestSellers: string, onlineExclusive: string }} labels
	 * @returns {string}
	 */
	function buildTagsBadgesHtml(tags, labels) {
		if (!tags) {
			return '';
		}
		let badgeHtml = '';
		if (tags.primaryTag === 'promo') {
			badgeHtml = '<span class="product-tag__badge promo_badge">' + escapeHtml(labels.promo) + '</span>';
		} else if (tags.newIn) {
			badgeHtml = '<span class="product-tag__badge">' + escapeHtml(labels.newIn) + '</span>';
		} else if (tags.bestSellers) {
			badgeHtml = '<span class="product-tag__badge">' + escapeHtml(labels.bestSellers) + '</span>';
		}
		if (!badgeHtml) {
			return '';
		}
		return '<div class="product-tags__badges-container">' + badgeHtml + '</div>';
	}

	/**
	 * @param {{ onlineExclusive: string }} labels
	 * @returns {string}
	 */
	function buildOnlineExclusiveBadgeHtml(labels) {
		return (
			'<div class="product-tags__badges-online-only">' +
			'<span class="product-tags__badges-online-only-text" aria-hidden="true">' +
			escapeHtml(labels.onlineExclusive) +
			'</span></div>'
		);
	}

	/**
	 * @param {Object} tags
	 * @returns {string}
	 */
	function buildCertificationBadgesHtml(tags) {
		let badges = tags && tags.certificationBadges;
		if (!badges || !badges.length) {
			return '';
		}
		let itemsHtml = badges
			.filter(function (badge) {
				return badge && badge.url;
			})
			.map(function (badge) {
				let kind = badge.kind ? ' product-tags__certification-badge--' + escapeHtml(badge.kind) : '';
				return (
					'<img class="product-tags__certification-badge' +
					kind +
					'" src="' +
					escapeHtml(badge.url) +
					'" alt="' +
					escapeHtml(badge.alt || '') +
					'" />'
				);
			})
			.join('');
		if (!itemsHtml) {
			return '';
		}
		return '<div class="product-tags__certification-badges">' + itemsHtml + '</div>';
	}

	/**
	 * @param {import('swiper').default} mainSwiper
	 * @param {number} idx
	 */
	function goToThumbIndex(mainSwiper, idx) {
		if (mainSwiper.params.loop) {
			mainSwiper.slideToLoop(idx);
		} else {
			mainSwiper.slideTo(idx);
		}
	}

	/**
	 * @param {Object} image
	 * @param {boolean} isFirst
	 * @param {number} index
	 * @param {number} total
	 * @param {string} thumbPattern
	 * @returns {string}
	 */
	function buildThumbSlideHtml(image, isFirst, index, total, thumbPattern) {
		let alt = image.alt
			? image.alt + ' image number ' + (image.index != null ? image.index : '')
			: 'image number ' + (image.index != null ? image.index : '');
		let thumbLabel = formatCarouselLabel(thumbPattern, index + 1, total);
		return (
			'<div class="swiper-slide' +
			(isFirst ? ' active' : '') +
			'" role="button" tabindex="0" aria-label="' +
			escapeHtml(thumbLabel) +
			'" aria-current="' +
			(isFirst ? 'true' : 'false') +
			'"><img src="' +
			escapeHtml(image.url) +
			'" alt="' +
			escapeHtml(alt) +
			'" itemprop="image" /></div>'
		);
	}

	/**
	 * @param {Element} gallery
	 * @returns {boolean}
	 */
	function commerceBadgesEnabled(gallery) {
		return !gallery || gallery.getAttribute('data-lvz-commerce-badges') !== 'false';
	}

	/**
	 * @param {Object} image
	 * @param {number} index
	 * @param {Object} tags
	 * @param {{ promo: string, newIn: string, bestSellers: string, onlineExclusive: string }} labels
	 * @param {boolean} showCommerceBadges
	 * @returns {string}
	 */
	function buildMainSlideHtml(image, index, tags, labels, showCommerceBadges) {
		let alt = image.alt
			? image.alt + ' image number ' + (image.index != null ? image.index : '')
			: 'image number ' + (image.index != null ? image.index : '');
		// Match productTagsBadges.isml: promo/newIn/bestSellers chip on every slide; online exclusive on first only.
		let badgesHtml = showCommerceBadges ? buildTagsBadgesHtml(tags, labels) : '';
		let onlineExclusiveHtml =
			showCommerceBadges && index === 0 && tags && tags.onlineExclusive
				? buildOnlineExclusiveBadgeHtml(labels)
				: '';
		let certificationHtml = buildCertificationBadgesHtml(tags);
		return (
			'<div class="swiper-slide' +
			(index === 0 ? ' active' : '') +
			'"><img src="' +
			escapeHtml(image.url) +
			'" alt="' +
			escapeHtml(alt) +
			'" itemprop="image" />' +
			badgesHtml +
			onlineExclusiveHtml +
			certificationHtml +
			'</div>'
		);
	}

	/**
	 * @param {Element} gallery
	 */
	function destroyOneGallery(gallery) {
		if (gallery._lvzMainSwiper && typeof gallery._lvzMainSwiper.destroy === 'function') {
			gallery._lvzMainSwiper.destroy(true, true);
		}
		if (gallery._lvzThumbsSwiper && typeof gallery._lvzThumbsSwiper.destroy === 'function') {
			gallery._lvzThumbsSwiper.destroy(true, true);
		}
		delete gallery._lvzMainSwiper;
		delete gallery._lvzThumbsSwiper;
		gallery.removeAttribute('data-lvz-pdp-gallery-init');

		let thumbsRoot = gallery.querySelector('.gallery__miniatures');
		if (thumbsRoot) {
			thumbsRoot.classList.remove('gallery__miniatures--scrollable');
		}
	}

	/**
	 * @param {Element} gallery
	 * @param {Object[]} images
	 * @param {Object} tags
	 */
	function rebuildGallerySlides(gallery, images, tags) {
		let thumbsWrapper = gallery.querySelector('.gallery__miniatures-swiper .swiper-wrapper');
		let mainWrapper = gallery.querySelector('.gallery__view .swiper .swiper-wrapper');
		if (!thumbsWrapper || !mainWrapper) {
			return;
		}

		let labels = getBadgeLabels(gallery);
		let showCommerceBadges = commerceBadgesEnabled(gallery);
		let thumbPattern = gallery.getAttribute('data-thumb-label-pattern') || 'Show image {0} of {1}';
		let thumbsHtml = '';
		let mainHtml = '';

		images.forEach(function (image, index) {
			thumbsHtml += buildThumbSlideHtml(image, index === 0, index, images.length, thumbPattern);
			mainHtml += buildMainSlideHtml(image, index, tags, labels, showCommerceBadges);
		});

		thumbsWrapper.innerHTML = thumbsHtml;
		mainWrapper.innerHTML = mainHtml;
	}

	/**
	 * @param {Element} gallery
	 * @param {number} realIndex
	 */
	function setActiveThumbClass(gallery, realIndex) {
		gallery.querySelectorAll('.gallery__miniatures-swiper .swiper-slide').forEach(function (slide, i) {
			slide.classList.toggle('active', i === realIndex);
			slide.setAttribute('aria-current', i === realIndex ? 'true' : 'false');
		});
	}

	/**
	 * PDP Gallery: utag.link on slide change (click, swipe, pagination, keyboard).
	 *
	 * @param {Element} gallery
	 * @param {number} realIndex
	 */
	function emitGalleryViewTracking(gallery, realIndex) {
		if (!gallery || typeof realIndex !== 'number' || realIndex < 0) {
			return;
		}

		if (window.lavazzaGtm && window.lavazzaGtm.media) {
			window.lavazzaGtm.media.trackPdpGalleryView({
				label: gallery.getAttribute('data-product-name') || '',
				category: gallery.getAttribute('data-product-id') || '',
				position: String(realIndex + 1),
			});
		}
	}

	/**
	 * @param {Element} gallery
	 * @param {import('swiper').default} mainSwiper
	 */
	function bindThumbSlides(gallery, mainSwiper) {
		let thumbSlides = gallery.querySelectorAll('.gallery__miniatures-swiper .swiper-slide');

		thumbSlides.forEach(function (slide, idx) {
			slide.style.cursor = 'pointer';

			slide.addEventListener('click', function () {
				goToThumbIndex(mainSwiper, idx);
			});

			slide.addEventListener('keydown', function (e) {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					goToThumbIndex(mainSwiper, idx);
				}
			});
		});
	}

	/**
	 * @param {Element} gallery
	 * @param {Element} thumbsSwiperEl
	 * @param {Element|null} thumbsPrev
	 * @param {Element|null} thumbsNext
	 * @param {function(number): void} scheduleTrackedGalleryView
	 * @returns {import('swiper').default|null}
	 */
	function createThumbsSwiperIfNeeded(gallery, thumbsSwiperEl, thumbsPrev, thumbsNext, scheduleTrackedGalleryView) {
		let thumbSlideCount = thumbsSwiperEl.querySelectorAll('.swiper-slide').length;
		let thumbsRoot = gallery.querySelector('.gallery__miniatures');

		if (thumbSlideCount >= 5 && thumbsRoot) {
			thumbsRoot.classList.add('gallery__miniatures--scrollable');
		}

		if (thumbSlideCount < 4) {
			return null;
		}

		return new Swiper(thumbsSwiperEl, {
			modules: [Navigation],
			direction: 'vertical',
			slidesPerView: 4,
			spaceBetween: 8,
			watchSlidesProgress: true,
			watchOverflow: true,
			navigation:
				thumbsPrev && thumbsNext
					? {
							nextEl: thumbsNext,
							prevEl: thumbsPrev,
							addIcons: false,
					  }
					: undefined,
			on: {
				slideChange: function onThumbsSlideChange(sw) {
					scheduleTrackedGalleryView(sw.realIndex);
				},
			},
		});
	}

	/**
	 * @param {Element} slide
	 * @param {string} badgesHtml
	 * @param {Element|null} img
	 */
	function syncSlideCommerceBadges(slide, badgesHtml, img) {
		let existingBadges = slide.querySelector('.product-tags__badges-container');

		if (existingBadges) {
			if (badgesHtml) {
				existingBadges.outerHTML = badgesHtml;
			} else {
				existingBadges.remove();
			}
			return;
		}

		if (badgesHtml && img) {
			img.insertAdjacentHTML('afterend', badgesHtml);
		}
	}

	/**
	 * @param {Element} slide
	 * @param {string} onlineHtml
	 * @param {Element|null} img
	 */
	function syncSlideOnlineExclusiveBadge(slide, onlineHtml, img) {
		let existingOnline = slide.querySelector('.product-tags__badges-online-only');
		let badgesAnchor = slide.querySelector('.product-tags__badges-container');

		if (existingOnline) {
			if (onlineHtml) {
				existingOnline.outerHTML = onlineHtml;
			} else {
				existingOnline.remove();
			}
			return;
		}

		if (!onlineHtml) {
			return;
		}

		let insertAfter = badgesAnchor || img;
		if (insertAfter) {
			insertAfter.insertAdjacentHTML('afterend', onlineHtml);
		} else {
			slide.insertAdjacentHTML('beforeend', onlineHtml);
		}
	}

	/**
	 * @param {Element} slide
	 * @param {string} certificationHtml
	 * @param {Element|null} img
	 */
	function syncSlideCertificationBadges(slide, certificationHtml, img) {
		let existing = slide.querySelector('.product-tags__certification-badges');

		if (existing) {
			if (certificationHtml) {
				existing.outerHTML = certificationHtml;
			} else {
				existing.remove();
			}
			return;
		}

		if (!certificationHtml) {
			return;
		}

		let insertAfter =
			slide.querySelector('.product-tags__badges-online-only') ||
			slide.querySelector('.product-tags__badges-container') ||
			img;
		if (insertAfter) {
			insertAfter.insertAdjacentHTML('afterend', certificationHtml);
		} else {
			slide.insertAdjacentHTML('beforeend', certificationHtml);
		}
	}

	/**
	 * @param {Element} gallery
	 */
	function initOneGallery(gallery) {
		if (gallery.getAttribute('data-lvz-pdp-gallery-init') === 'true') {
			return;
		}

		let viewEl = gallery.querySelector('.gallery__view .swiper');
		let thumbsRoot = gallery.querySelector('.gallery__miniatures');
		let thumbsSwiperEl = thumbsRoot ? thumbsRoot.querySelector('.gallery__miniatures-swiper') : null;

		if (!viewEl || !thumbsRoot || !thumbsSwiperEl) {
			return;
		}

		let slideCount = viewEl.querySelectorAll('.swiper-slide').length;
		if (slideCount < 1) {
			return;
		}

		gallery.classList.toggle('gallery--single-slide', slideCount === 1);

		let paginationEl = gallery.querySelector('.swiper-pagination');
		let viewNext = gallery.querySelector('.gallery__view .swiper-button-next');
		let viewPrev = gallery.querySelector('.gallery__view .swiper-button-prev');
		let thumbsPrev = thumbsRoot.querySelector('.gallery__miniatures-nav--prev');
		let thumbsNext = thumbsRoot.querySelector('.gallery__miniatures-nav--next');
		let liveEl = gallery.querySelector('.gallery__live');
		let paginationPattern = gallery.getAttribute('data-dot-label-pattern') || 'Image {0} of {1}';
		let livePattern = gallery.getAttribute('data-live-label-pattern') || paginationPattern;

		// Swiper's own loop-mode position correction fires slideChange on setup, and for one
		// user click/swipe it can fire slideChange twice — once mid-transition, once corrected
		// to the settled real index, sometimes with different realIndex values. Neither the
		// setup event nor the transitional one is the user "scrolling/clicking to view the
		// images" the tracking requirement asks for.
		let isGalleryReadyForTracking = false;
		let trackingDebounceTimer = null;

		/**
		 * @param {number} realIndex
		 */
		function scheduleTrackedGalleryView(realIndex) {
			if (!isGalleryReadyForTracking) {
				return;
			}

			if (trackingDebounceTimer) {
				window.clearTimeout(trackingDebounceTimer);
			}

			trackingDebounceTimer = window.setTimeout(function trackSettledSlide() {
				trackingDebounceTimer = null;
				emitGalleryViewTracking(gallery, realIndex);
			}, 200);
		}

		let thumbsSwiperInstance = createThumbsSwiperIfNeeded(
			gallery,
			thumbsSwiperEl,
			thumbsPrev,
			thumbsNext,
			scheduleTrackedGalleryView
		);

		let mainModules = [Navigation, Pagination, A11y, Keyboard];
		if (thumbsSwiperInstance) {
			mainModules.push(Thumbs);
		}

		//Gallery view - Swiper configuration
		let mainConfig = {
			modules: mainModules,
			snapToSlideEdge: true,
			slidesPerView: 'auto',
			spaceBetween: 16,
			loop: slideCount > 1,
			resizeObserver: false,
			a11y: {
				enabled: true,
			},
			keyboard: {
				enabled: true,
				onlyInViewport: true,
			},
			pagination: paginationEl
				? {
						el: paginationEl,
						clickable: true,
				  }
				: undefined,
			navigation:
				viewPrev && viewNext
					? {
							nextEl: viewNext,
							prevEl: viewPrev,
							addIcons: false,
					  }
					: undefined,
			breakpoints: {
				1024: {
					slidesPerView: 'auto',
					spaceBetween: 14,
				},
			},
			on: {
				init: function onInit(sw) {
					decorateCarouselPagination(gallery, '.swiper-pagination', paginationPattern, slideCount);
					updateCarouselLiveRegion(liveEl, livePattern, sw.realIndex, slideCount);

					// Let loop mode's own setup-time slideChange (if any) fire and be ignored
					// before accepting tracked changes — real user interaction happens well
					// after this window.
					window.setTimeout(function markGalleryReadyForTracking() {
						isGalleryReadyForTracking = true;
					}, 50);
				},
				slideChange: function onSlideChange(sw) {
					updateCarouselLiveRegion(liveEl, livePattern, sw.realIndex, slideCount);
					setActiveThumbClass(gallery, sw.realIndex);
					scheduleTrackedGalleryView(sw.realIndex);
				},
				breakpoint: function onBreakpoint() {
					decorateCarouselPagination(gallery, '.swiper-pagination', paginationPattern, slideCount);
				},
			},
		};

		if (thumbsSwiperInstance) {
			mainConfig.thumbs = {
				swiper: thumbsSwiperInstance,
			};
		}

		let mainSwiper = new Swiper(viewEl, mainConfig);
		gallery._lvzMainSwiper = mainSwiper;
		gallery._lvzThumbsSwiper = thumbsSwiperInstance;

		if (slideCount > 1) {
			bindThumbSlides(gallery, mainSwiper);
			setActiveThumbClass(gallery, mainSwiper.realIndex);
		}

		gallery.setAttribute('data-lvz-pdp-gallery-init', 'true');

		requestAnimationFrame(function () {
			mainSwiper.update();
			if (thumbsSwiperInstance) {
				thumbsSwiperInstance.update();
			}
			stickySidebar.updateStickySidebar();
		});
	}

	/**
	 * @param {{ container?: JQuery }} response
	 * @returns {Element|null}
	 */
	function resolvePdpGallery(response) {
		let gallery = null;
		if (response && response.container && response.container.length) {
			let $container = response.container;
			if ($container.closest('#chooseBonusProductModal').length) {
				return null;
			}
			let $pdp = $container.hasClass('product-detail') ? $container : $container.closest('.product-detail');
			if (!$pdp.length) {
				return null;
			}
			gallery = $pdp.find('.gallery.primary-images').get(0);
			return gallery || null;
		}
		if (!gallery) {
			gallery = document.querySelector('.product-detail .gallery.primary-images');
		}
		return gallery;
	}

	/**
	 * @param {Element} gallery
	 * @param {Object} tags
	 */
	function updatePdpBadgesOnly(gallery, tags) {
		let labels = getBadgeLabels(gallery);
		let slides = gallery.querySelectorAll('.gallery__view .swiper .swiper-wrapper > .swiper-slide');
		let showCommerceBadges = commerceBadgesEnabled(gallery);

		if (!showCommerceBadges) {
			gallery.querySelectorAll('.product-tags__badges-container, .product-tags__badges-online-only').forEach(function (el) {
				el.remove();
			});
		}

		slides.forEach(function (slide, index) {
			let img = slide.querySelector('img[itemprop="image"]') || slide.querySelector(':scope > img');
			if (showCommerceBadges) {
				syncSlideCommerceBadges(slide, buildTagsBadgesHtml(tags, labels), img);
				let onlineHtml = index === 0 && tags && tags.onlineExclusive ? buildOnlineExclusiveBadgeHtml(labels) : '';
				syncSlideOnlineExclusiveBadge(slide, onlineHtml, img);
			}
			syncSlideCertificationBadges(slide, buildCertificationBadgesHtml(tags), img);
		});
	}

	/**
	 * @param {{ data?: { product?: { tags?: Object } }, container?: JQuery }} response
	 */
	function updatePdpBadgesFromVariation(response) {
		if (!response || !response.data || !response.data.product) {
			return;
		}

		let gallery = resolvePdpGallery(response);
		if (!gallery) {
			return;
		}

		updatePdpBadgesOnly(gallery, response.data.product.tags || {});
	}

	/**
	 * @param {{ data?: { product?: { images?: { large?: Object[] }, tags?: Object } }, container?: JQuery }} response
	 */
	function updateGalleryFromVariation(response) {
		if (!response || !response.data || !response.data.product) {
			return;
		}

		let images = response.data.product.images && response.data.product.images.large;
		if (!images || !images.length) {
			return;
		}

		let gallery = resolvePdpGallery(response);
		if (!gallery) {
			return;
		}

		let tags = response.data.product.tags || {};
		destroyOneGallery(gallery);
		rebuildGallerySlides(gallery, images, tags);
		initOneGallery(gallery);
	}

	/**
	 * @param {Element|null} gallery
	 * @param {{ large?: Object[] }} [productImages] - images of the Product-Variation response
	 * @returns {boolean} true when the response carries a different set of main images
	 */
	function galleryImagesChanged(gallery, productImages) {
		let images = productImages && productImages.large;
		if (!gallery || !images || !images.length) {
			return false;
		}

		// Compare as sets: Swiper's loop mode physically reorders the slides in the DOM, so a
		// position-by-position check reports a change (and re-animates the gallery) on every call.
		let current = {};
		gallery.querySelectorAll('.gallery__view .swiper .swiper-slide > img').forEach(function (img) {
			current[img.getAttribute('src')] = true;
		});
		let incoming = {};
		images.forEach(function (image) {
			incoming[image.url] = true;
		});

		let currentUrls = Object.keys(current);
		return (
			currentUrls.length !== Object.keys(incoming).length ||
			currentUrls.some(function (url) {
				return !incoming[url];
			})
		);
	}

	function initPdpGallery() {
		document.querySelectorAll('.gallery.primary-images').forEach(function (gallery) {
			initOneGallery(gallery);
		});
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', initPdpGallery);
	} else {
		initPdpGallery();
	}

	$('body').on('product:beforeAttributeSelect', function () {
		swatchGalleryUpdateForRequest = $('body').data('lvz-swatch-gallery-update') === true;
		$('body').removeData('lvz-swatch-gallery-update');
	});

	$('body').on('product:afterAttributeSelect', function (e, response) {
		if (!response || !response.data || !response.data.product) {
			return;
		}

		// Color swatches always rebuild. Any other selection (pack size / type) rebuilds only when
		// the variant's images differ, so quantity changes do not reset the carousel.
		let forceUpdate = swatchGalleryUpdateForRequest;
		swatchGalleryUpdateForRequest = false;

		if (forceUpdate || galleryImagesChanged(resolvePdpGallery(response), response.data.product.images)) {
			updateGalleryFromVariation(response);
			updatePdpBadgesFromVariation(response);
		}
	});
})();
